import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

import { useResolvedTheme } from '../theme';
import Modal, { Button, Check, Field, Pill, inputClass } from './Modal';
import {
  tasksFor,
  usePlanActions,
  useTaskActions,
  useTasks,
  useWritableCalendars
} from './data';
import type { Plan, PlanBlock, Rhythm } from './types';
import { minutesLabel, timeRange, tint } from './util';

interface Props {
  block: PlanBlock;
  day: string;
  rhythm: Rhythm;
  plan: Plan;
  onClose: () => void;
  onOpenRhythm: () => void;
}

const STATE: Record<PlanBlock['type'], { label: string; tone: 'muted' | 'accent' | 'warn' }> = {
  proposal: { label: 'Suggested', tone: 'muted' },
  pin: { label: 'Pinned', tone: 'accent' },
  done: { label: 'Done', tone: 'accent' },
  missed: { label: 'Missed', tone: 'warn' }
};

function toLocalInput(d: Date): string {
  return format(d, 'HH:mm');
}

export default function BlockModal({ block, day, rhythm, plan, onClose, onOpenRhythm }: Props) {
  const theme = useResolvedTheme();
  const t = tint(rhythm.color, theme);
  const actions = usePlanActions();

  const origStart = new Date(block.start);
  const origMinutes = Math.round((new Date(block.end).getTime() - origStart.getTime()) / 60_000);
  const [startStr, setStartStr] = useState(toLocalInput(origStart));
  const [minutes, setMinutes] = useState(origMinutes);
  const start = useMemo(() => {
    const [h, m] = startStr.split(':').map(Number);
    const d = parseISO(day);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
  }, [startStr, day]);
  const end = new Date(start.getTime() + minutes * 60_000);
  const moved = startStr !== toLocalInput(origStart) || minutes !== origMinutes;

  const [googleOpen, setGoogleOpen] = useState(rhythm.social && block.type !== 'done');
  const busy =
    actions.log.isPending ||
    actions.pin.isPending ||
    actions.movePin.isPending ||
    actions.commit.isPending ||
    actions.unpin.isPending ||
    actions.unlog.isPending ||
    actions.skip.isPending;
  const error =
    actions.commit.error ?? actions.log.error ?? actions.pin.error ?? actions.movePin.error ?? null;

  const state = block.overdue && block.type === 'proposal' ? { label: 'Catch-up', tone: 'warn' as const } : STATE[block.type];

  async function markDone() {
    const now = Date.now();
    // A session marked done before its slot happened just now.
    const s = start.getTime() <= now ? start : new Date(now - minutes * 60_000);
    await actions.log.mutateAsync({
      rhythm_id: rhythm.id,
      start: s.toISOString(),
      minutes,
      pin_id: block.type === 'pin' || block.type === 'missed' ? block.id : undefined
    });
    onClose();
  }

  async function pinHere() {
    if (block.type === 'pin') {
      await actions.movePin.mutateAsync({ id: block.id, start: start.toISOString(), end: end.toISOString() });
    } else {
      await actions.pin.mutateAsync({ rhythm_id: rhythm.id, start: start.toISOString(), end: end.toISOString() });
    }
    onClose();
  }

  const footer = (() => {
    switch (block.type) {
      case 'done':
        return (
          <>
            <Button variant="danger" disabled={busy} onClick={() => actions.unlog.mutate(block.id, { onSuccess: onClose })}>
              Undo
            </Button>
            <span className="flex-1" />
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </>
        );
      case 'missed':
        return (
          <>
            <Button variant="danger" disabled={busy} onClick={() => actions.unpin.mutate(block.id, { onSuccess: onClose })}>
              Remove
            </Button>
            <span className="flex-1" />
            <Button variant="primary" disabled={busy} onClick={markDone} data-autofocus>
              I did it
            </Button>
          </>
        );
      case 'pin':
        return (
          <>
            <Button variant="danger" disabled={busy} onClick={() => actions.unpin.mutate(block.id, { onSuccess: onClose })}>
              Unpin
            </Button>
            <span className="flex-1" />
            {moved && (
              <Button variant="outline" disabled={busy} onClick={pinHere}>
                Save time
              </Button>
            )}
            {!googleOpen && (
              <Button variant="outline" onClick={() => setGoogleOpen(true)}>
                Add to Google
              </Button>
            )}
            <Button variant="primary" disabled={busy} onClick={markDone} data-autofocus>
              Done
            </Button>
          </>
        );
      default:
        return (
          <>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() =>
                actions.skip.mutate({ rhythm_id: rhythm.id, day, skipped: true }, { onSuccess: onClose })
              }
            >
              Skip {day === plan.today ? 'today' : format(parseISO(day), 'EEEE')}
            </Button>
            <span className="flex-1" />
            {!googleOpen && (
              <Button variant="outline" onClick={() => setGoogleOpen(true)}>
                Add to Google
              </Button>
            )}
            <Button variant="outline" disabled={busy} onClick={pinHere}>
              Pin
            </Button>
            <Button variant="primary" disabled={busy} onClick={markDone} data-autofocus>
              Done
            </Button>
          </>
        );
    }
  })();

  return (
    <Modal
      title={rhythm.name}
      accent={t.solid}
      badge={<Pill tone={state.tone}>{state.label}</Pill>}
      subtitle={format(parseISO(day), 'EEEE, MMMM d')}
      onClose={onClose}
      footer={footer}
      width={540}
    >
      <div className="flex flex-col gap-5">
        <TimeRow
          start={startStr}
          onStart={setStartStr}
          minutes={minutes}
          onMinutes={setMinutes}
          range={timeRange(start, end)}
          locked={block.type === 'done'}
        />

        {googleOpen && block.type !== 'done' && (
          <GoogleSection
            rhythm={rhythm}
            busy={busy}
            onCommit={(title, calendarId) =>
              actions.commit.mutate(
                {
                  rhythm_id: rhythm.id,
                  start: start.toISOString(),
                  end: end.toISOString(),
                  title,
                  calendar_id: calendarId,
                  pin_id: block.type === 'pin' || block.type === 'missed' ? block.id : undefined
                },
                { onSuccess: onClose }
              )
            }
          />
        )}

        {rhythm.task_tags.length > 0 && (
          <TaskSection rhythm={rhythm} day={day} plan={plan} color={t.solid} />
        )}

        {error && <div className="rounded-md bg-[var(--today-bg)] px-3 py-2 text-[0.82rem] text-[var(--today-fg)]">{error.message}</div>}

        <button
          type="button"
          onClick={onOpenRhythm}
          className="self-start text-[0.8rem] text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text)] hover:underline"
        >
          Edit {rhythm.name}
        </button>
      </div>
    </Modal>
  );
}

function TimeRow({
  start,
  onStart,
  minutes,
  onMinutes,
  range,
  locked
}: {
  start: string;
  onStart: (v: string) => void;
  minutes: number;
  onMinutes: (v: number) => void;
  range: string;
  locked: boolean;
}) {
  if (locked) {
    return (
      <div className="flex items-baseline gap-3">
        <span className="text-[1.25rem] font-semibold tabular-nums">{range}</span>
        <span className="text-[0.85rem] text-[var(--text-muted)]">{minutesLabel(minutes)}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-4">
      <Field label="Starts">
        <input type="time" step={900} value={start} onChange={(e) => onStart(e.target.value)} className={clsx(inputClass, 'w-[120px] tabular-nums')} />
      </Field>
      <Field label="Length">
        <div className="flex items-center rounded-md border border-[var(--border-strong)] bg-[var(--bg)]">
          <button type="button" className="px-3 py-[6px] text-[var(--text-muted)] hover:text-[var(--text)]" onClick={() => onMinutes(Math.max(15, minutes - 15))} aria-label="shorter">
            −
          </button>
          <span className="min-w-[64px] text-center text-[0.88rem] tabular-nums">{minutesLabel(minutes)}</span>
          <button type="button" className="px-3 py-[6px] text-[var(--text-muted)] hover:text-[var(--text)]" onClick={() => onMinutes(Math.min(16 * 60, minutes + 15))} aria-label="longer">
            +
          </button>
        </div>
      </Field>
      <span className="pb-[7px] text-[0.88rem] text-[var(--text-muted)] tabular-nums">{range}</span>
    </div>
  );
}

function GoogleSection({
  rhythm,
  busy,
  onCommit
}: {
  rhythm: Rhythm;
  busy: boolean;
  onCommit: (title: string, calendarId: string | null) => void;
}) {
  const cals = useWritableCalendars();
  const [title, setTitle] = useState(rhythm.name);
  const [calendarId, setCalendarId] = useState<string>('');
  const terms = [rhythm.name, ...rhythm.match_terms];
  const counts = terms.some((term) =>
    new RegExp(`(^|[^\\w])${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\w])`, 'i').test(title)
  );
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg)] p-3">
      <div className="text-[0.8rem] font-medium">Put it on Google Calendar</div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <input
          className={clsx(inputClass, 'w-full')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="event title"
        />
        <select
          className={clsx(inputClass, 'max-w-[180px]')}
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          aria-label="calendar"
        >
          <option value="">Default calendar</option>
          {(cals.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.summary}
            </option>
          ))}
        </select>
      </div>
      {!counts && (
        <div className="text-[0.75rem] text-[var(--today-fg)]">
          Keep “{rhythm.name}”{rhythm.match_terms.length ? ' or one of its calendar words' : ''} in the title so it counts.
        </div>
      )}
      <div className="flex justify-end">
        <Button variant="primary" disabled={busy || !title.trim()} onClick={() => onCommit(title.trim(), calendarId || null)}>
          Create event
        </Button>
      </div>
    </div>
  );
}

function TaskSection({ rhythm, day, plan, color }: { rhythm: Rhythm; day: string; plan: Plan; color: string }) {
  const tasksQ = useTasks();
  const ta = useTaskActions();
  const [draft, setDraft] = useState('');
  const [showAll, setShowAll] = useState(false);
  const all = tasksFor(tasksQ.data?.tasks, rhythm);
  const byKey = new Map(all.map((t) => [t.key, t]));
  const assignedHere = plan.assignments.filter((a) => a.rhythm_id === rhythm.id && a.day === day);
  const hereKeys = new Set(assignedHere.map((a) => a.key));
  const elsewhere = new Map(plan.assignments.filter((a) => !hereKeys.has(a.key)).map((a) => [a.key, a]));
  const pool = all.filter((t) => t.status === 'open' && !hereKeys.has(t.key));
  const shown = showAll ? pool : pool.slice(0, 6);

  if (tasksQ.data && !tasksQ.data.available) {
    return <div className="text-[0.82rem] text-[var(--text-muted)]">The Obsidian vault isn’t reachable.</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <span className="text-[0.72rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">To-dos</span>
        <span className="flex gap-1">
          {rhythm.task_tags.map((tag) => (
            <span key={tag} className="rounded bg-[var(--bg-hover)] px-[6px] text-[0.72rem] text-[var(--text-muted)]">
              #{tag}
            </span>
          ))}
        </span>
      </div>

      <div className="flex flex-col gap-[2px]">
        {assignedHere.map((a) => {
          const task = byKey.get(a.key);
          const done = task?.status === 'done';
          return (
            <div key={a.key} className="group flex items-center gap-[10px] rounded-md px-1 py-[5px] hover:bg-[var(--bg-hover)]">
              <Check
                checked={done}
                color={color}
                onChange={() => task && ta.done.mutate({ key: a.key, done: !done })}
              />
              <span className={clsx('min-w-0 flex-1 text-[0.88rem]', done && 'text-[var(--text-faint)] line-through')}>
                {task?.text ?? a.text}
              </span>
              <button
                type="button"
                onClick={() => ta.assign.mutate({ key: a.key, text: a.text, rhythm_id: null, day: null })}
                aria-label="take off this block"
                className="px-1 text-[var(--text-faint)] opacity-0 hover:text-[var(--text)] group-hover:opacity-100"
              >
                ×
              </button>
            </div>
          );
        })}
        <form
          className="flex items-center gap-[10px] px-1 py-[3px]"
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            ta.add.mutate({ text, tag: rhythm.task_tags[0], rhythm_id: rhythm.id, day });
            setDraft('');
          }}
        >
          <span className="grid h-[18px] w-[18px] flex-shrink-0 place-items-center text-[var(--text-faint)]">+</span>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`New to-do #${rhythm.task_tags[0]}`}
            className="min-w-0 flex-1 bg-transparent py-[2px] text-[0.88rem] outline-none placeholder:text-[var(--text-faint)]"
          />
        </form>
      </div>

      {pool.length > 0 && (
        <div className="flex flex-col gap-[2px] rounded-lg bg-[var(--bg)] p-2">
          <div className="px-1 pb-1 text-[0.72rem] text-[var(--text-faint)]">Open in Obsidian</div>
          {shown.map((task) => {
            const other = elsewhere.get(task.key);
            return (
              <button
                key={task.key}
                type="button"
                onClick={() => ta.assign.mutate({ key: task.key, text: task.text, rhythm_id: rhythm.id, day })}
                className="group flex items-center gap-[10px] rounded-md px-1 py-[5px] text-left hover:bg-[var(--bg-hover)]"
                title={task.path}
              >
                <span className="grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-[5px] border border-dashed border-[var(--border-strong)] text-[0.75rem] text-[var(--text-faint)] group-hover:border-[var(--accent)] group-hover:text-[var(--accent)]">
                  +
                </span>
                <span className="min-w-0 flex-1 truncate text-[0.86rem]">{task.text}</span>
                {other && (
                  <span className="flex-shrink-0 text-[0.72rem] text-[var(--text-faint)]">
                    {format(parseISO(other.day), 'EEE MMM d')}
                  </span>
                )}
              </button>
            );
          })}
          {pool.length > shown.length && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="px-1 pt-1 text-left text-[0.78rem] text-[var(--text-muted)] hover:text-[var(--text)]"
            >
              {pool.length - shown.length} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
